import { IconLock } from "@tabler/icons-react";
import { useRouter } from "next/router";
import { useState } from "react";

import { AIButton } from "~/components/_design-system/button/AIButton";
import { AssistantExperience } from "~/components/domain/assistant/AssistantExperience";
import { useUI } from "~/contexts/UIProvider";
import { GRAY_PALETTE } from "~/styles/palettes";
import { Flex, Indicator, Popover, Text, useHotkeys } from "~/ui";

import styles from "./AIChat.module.css";
import { AIChatErrorBoundary } from "./AIChatErrorBoundary";

/**
 * Manual expansion never serializes or replays a prompt. The app-scoped conversation store keeps
 * the active conversation, unsent draft, and exact review target while the route changes.
 */
export const assistantExpandRoute = (
  orgSlug: string | string[] | undefined,
): {
  pathname: string;
  query: Record<string, string | string[] | undefined>;
} => ({
  pathname: "/[orgSlug]/assistant",
  query: { orgSlug },
});

export const AIChat = () => {
  const ui = useUI();
  const router = useRouter();
  const [opened, setOpened] = useState(false);

  useHotkeys([["a", () => setOpened(o => !o)]]);

  const buttonLabel = ui.isMobile ? undefined : "Ask AI";
  const buttonSize = ui.isMobile ? "md" : "xs";
  const popoverHeight = ui.isMobile ? "90vh" : "70vh";
  const popoverWidth = ui.isMobile ? "100%" : 420;
  const experienceHeight = ui.isMobile ? "calc(90vh - 58px)" : "calc(70vh - 58px)";
  const handleExpand = async () => {
    const navigated = await router.push(assistantExpandRoute(router.query.orgSlug));
    if (navigated) {
      setOpened(false);
    }
  };

  return (
    <Popover
      keepMounted
      opened={opened}
      shadow="xl"
      transitionProps={{ transition: "pop", duration: 150 }}
      width="auto"
      withArrow
      withinPortal
      onChange={setOpened}
      dropdownClassName={styles.dropdown}
      arrowClassName={styles.arrow}
    >
      <Popover.Target>
        <Indicator color="pink" size={12} withBorder>
          <AIButton size={buttonSize} onClick={() => setOpened(o => !o)}>
            {buttonLabel}
          </AIButton>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown mah={popoverHeight} maw={popoverWidth} miw={popoverWidth} p={0}>
        {/* Main content area - fills available space */}
        <Flex direction="column" style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <AIChatErrorBoundary>
            <AssistantExperience
              surface="ask_ai_popover"
              height={experienceHeight}
              isActive={opened}
              onExpand={() => void handleExpand()}
            />
          </AIChatErrorBoundary>
        </Flex>
        {/* Footer - always pinned at bottom */}
        <Flex
          align="center"
          justify="center"
          gap={6}
          px="md"
          py={10}
          mih={42}
          style={{ borderTop: `1px solid ${GRAY_PALETTE[2]}`, flexShrink: 0 }}
        >
          <IconLock aria-hidden size={12} color={GRAY_PALETTE[6]} />
          <Text align="center" c="gray.6" fz={11} lh={1.35}>
            Private to your organization · Never used for AI training
          </Text>
        </Flex>
      </Popover.Dropdown>
    </Popover>
  );
};
